<template>
  <b-card no-body class="dash-panel dash-chart-panel">
    <b-card-header class="dash-panel__header border-0 pb-0">
      <div class="dash-panel__heading">
        <span class="dash-panel__icon dash-panel__icon--collection">
          <feather-icon icon="TrendingUpIcon" size="18" />
        </span>
        <div>
          <b-card-title class="dash-panel__title mb-0">{{
            $t("Weekly Collection")
          }}</b-card-title>
          <small class="dash-panel__subtitle">{{
            $t("Last 7 days")
          }}</small>
        </div>
      </div>
    </b-card-header>

    <b-card-body class="pb-1">
      <div ref="chartViewport" class="dash-chart-viewport-inner">
      <div class="dash-chart-canvas">
        <div v-if="!chartReady" class="dash-chart-empty text-muted text-center py-5">
          {{ $t("Loading") }}...
        </div>
        <div v-else-if="!chartInView" class="dash-chart-placeholder dash-chart-placeholder--short">
          <span class="dash-chart-placeholder__pulse" />
        </div>
        <vue-apex-charts
          v-else
          :key="chartRenderKey()"
          type="bar"
          height="360"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BCardTitle } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import chartInViewMixin from "@/views/dashboard/ecommerce/mixins/chartInViewMixin";
import { dashApexTooltip } from "@/views/dashboard/ecommerce/utils/dashboardApexTooltip";

export default {
  mixins: [chartInViewMixin],
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    VueApexCharts,
  },
  props: {
    weeklyData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      chartSeries: [{ name: "Collection", data: [] }],
      chartOptions: {},
    };
  },
  computed: {
    chartReady() {
      return (
        this.weeklyData &&
        Array.isArray(this.weeklyData.data) &&
        this.weeklyData.data.length > 0
      );
    },
  },
  watch: {
    weeklyData: {
      deep: true,
      immediate: true,
      handler(val) {
        this.applyWeeklyData(val);
      },
    },
  },
  methods: {
    applyWeeklyData(wd) {
      if (!wd || !Array.isArray(wd.data)) return;

      const categories = [];
      const values = [];
      wd.data.forEach((el) => {
        categories.push(el.date);
        values.push(Number(el.collection) || 0);
      });

      const peak = Math.max(...values, 0);
      const yMax =
        Number(wd.highestCollection) > 0
          ? Number(wd.highestCollection)
          : peak > 0
            ? Math.ceil(peak * 1.18)
            : 1000;

      this.chartSeries = [{ name: "Collection", data: values }];
      this.chartOptions = {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "inherit",
          animations: this.apexStaggerAnimation(160),
        },
        plotOptions: {
          bar: {
            columnWidth: "42%",
            borderRadius: 10,
            borderRadiusApplication: "end",
          },
        },
        colors: ["#7c3aed"],
        fill: {
          type: "gradient",
          gradient: {
            type: "vertical",
            shadeIntensity: 0.35,
            gradientToColors: ["#c4b5fd"],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 0.85,
            stops: [0, 55, 100],
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#f1f5f9",
          strokeDashArray: 4,
          padding: { top: 8, right: 12, left: 8, bottom: 0 },
        },
        xaxis: {
          categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
          },
        },
        yaxis: {
          min: 0,
          max: yMax,
          tickAmount: 5,
          labels: {
            style: { colors: "#64748b", fontSize: "11px" },
            formatter(val) {
              if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
              if (val >= 1e3) return `${(val / 1e3).toFixed(0)}k`;
              return Math.round(val);
            },
          },
        },
        tooltip: dashApexTooltip({
          shared: false,
          x: {
            show: true,
            formatter(val) {
              return val != null ? String(val) : "";
            },
          },
        }),
        legend: { show: false },
      };

      this.bumpChartData();
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.dash-panel__icon--collection {
  background: #f5f3ff;
  color: #7c3aed;
}

.dash-chart-empty {
  font-size: 0.9rem;
  font-weight: 600;
}

.dash-chart-placeholder {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;

  &--short {
    min-height: 360px;
  }
}

.dash-chart-placeholder__pulse {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ede9fe, #7c3aed);
  opacity: 0.35;
  animation: dashWeeklyPulse 1.2s ease-in-out infinite;
}

@keyframes dashWeeklyPulse {
  0%,
  100% {
    transform: scaleY(0.35);
    border-radius: 16px 16px 6px 6px;
  }
  50% {
    transform: scaleY(1);
    border-radius: 10px 10px 10px 10px;
  }
}
</style>
